import type { Participant, ParticipantBehaviour, ParticipantQr } from '@piccolohealth/pbs-common';
import { ChakraV3, Empty, Spin } from '@piccolohealth/ui';
import { matchSorter, P } from '@piccolohealth/util';
import React from 'react';
import { FaPlus } from 'react-icons/fa';
import { showModal } from '../../components/generic/Modal';
import {
  useParticipantEmbeddableFormsQuery,
  useParticipantQrsQuery,
} from '../../graphql/hooks/useParticipantQuery';
import { usePermission } from '../../hooks/usePermission';
import { useParticipantQRsFilter } from './hooks/useParticipantQRsFilter';
import { ParticipantQRCreateModal } from './ParticipantQRCreateModal';
import { ParticipantQRFilterControls } from './ParticipantQRFilterControls';
import { ParticipantQRListItem } from './ParticipantQRListItem';

interface Props {
  participant: Participant;
  isDisabled?: boolean;
}

export const ParticipantQRCard = (props: Props) => {
  const { participant } = props;

  const createParticipantQRPermission = usePermission('create', 'participantQr');
  const isDisabled = props.isDisabled || !createParticipantQRPermission;
  const filter = useParticipantQRsFilter();

  const behavioursQuery = useParticipantQrsQuery({
    organizationId: participant.organizationId,
    participantId: participant.id,
  });

  const embeddableFormsQuery = useParticipantEmbeddableFormsQuery({
    organizationId: participant.organizationId,
    participantId: participant.id,
  });

  const participantBehaviours = (behavioursQuery.data?.organization?.participant?.behaviours ??
    []) as ParticipantBehaviour[];

  const participantEmbeddableForms =
    embeddableFormsQuery.data?.organization?.participant?.embeddableForms ?? [];

  const qrs = React.useMemo(() => {
    const results = (behavioursQuery.data?.organization?.participant?.qrs as ParticipantQr[]) ?? [];

    const nameFiltered = matchSorter(results, filter.nameFilter, {
      keys: ['name'],
    });

    const statusFiltered = !P.isEmpty(filter.statusFilter)
      ? nameFiltered.filter((v) => filter.statusFilter.includes(v.status))
      : nameFiltered;

    const labelFiltered = !P.isEmpty(filter.labelFilter)
      ? statusFiltered.filter((v) => v.labels?.some((l) => filter.labelFilter.includes(l.id)))
      : statusFiltered;

    return labelFiltered;
  }, [
    filter.labelFilter,
    filter.nameFilter,
    filter.statusFilter,
    behavioursQuery.data?.organization?.participant?.qrs,
  ]);

  const content = P.run(() => {
    if (behavioursQuery.isLoading || embeddableFormsQuery.isLoading) {
      return <Spin />;
    }

    if (P.isEmpty(qrs)) {
      return <Empty h='full' title='No QR codes found' p={0} />;
    }

    return (
      <ChakraV3.Stack gap={10}>
        {qrs.map((qr) => (
          <ParticipantQRListItem
            key={qr.id}
            qr={qr}
            participant={participant}
            participantBehaviours={participantBehaviours}
            participantEmbeddableForms={participantEmbeddableForms}
            isDisabled={isDisabled}
          />
        ))}
      </ChakraV3.Stack>
    );
  });

  return (
    <ChakraV3.Card.Root h='full'>
      <ChakraV3.Card.Header>
        <ChakraV3.Stack gap={0}>
          <ChakraV3.Heading size='xl' fontWeight='bold'>
            QR codes
          </ChakraV3.Heading>
          <ChakraV3.Text fontSize='sm' color='secondary'>
            {`Manage this participant's external team members`}
          </ChakraV3.Text>
        </ChakraV3.Stack>
      </ChakraV3.Card.Header>
      <ChakraV3.Card.Body>
        <ChakraV3.Stack gap={6}>
          <ParticipantQRFilterControls filter={filter} />
          {content}
          <ChakraV3.Button
            onClick={() =>
              showModal(ParticipantQRCreateModal, {
                participantId: participant.id,
              })
            }
            disabled={isDisabled}
            size='sm'
            variant='subtle'
          >
            <FaPlus />
            Add QR code
          </ChakraV3.Button>
        </ChakraV3.Stack>
      </ChakraV3.Card.Body>
    </ChakraV3.Card.Root>
  );
};
